import React, { useContext } from 'react';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, StaticContext, FourOhFourErrorPage, SurveyTheme } from '@sm/webassets';
import { t, defineMessages } from '@sm/intl';
import Layout from '~app/components/Survey/Layout';
import Header from '~app/components/Survey/Header';
import QuizScoreSummary from '~app/components/Survey/QuizScoreSummary';
import SurveyTitle from '~app/components/Survey/SurveyTitle';
import SurveyPages from '~app/components/Survey/SurveyPages';
import ActionGroup from '~app/components/Survey/ActionGroup';
import Footer from '~app/components/Survey/Footer';
import { getSurveyForCollector, getQuizSurveyWithResponsesQM } from './QuizResultQM.graphql';
import formatSurvey from './helper/transform';
import './quiz-result.scss';

const COPY = defineMessages({
  SM_META_DESCRIPTION: {
    id: 'QuizResult.MetaSmMetaDescription',
    defaultMessage: "Create your own online survey now with SurveyMonkey's expert certified FREE templates.",
    description: '[Type: label][Vis: med] - Trailing text for core product domain description meta tag',
  },
  META_DESCRIPTION: {
    id: 'QuizResult.MetaDescription',
    defaultMessage: 'Your feedback is appreciated!',
    description:
      "Web survey powered by SurveyMonkey.com. Create your own online survey now with SurveyMonkey's expert certified FREE templates.",
  },
  WEB_POWERED_BY: {
    id: 'QuizResult.MetaPoweredBy',
    defaultMessage: 'Web survey powered by {domainName}',
    description: '[Type: label][Vis: med] - Text for non core product domains in meta tag',
  },
});

const QuizResult = () => {
  const {
    sm,
    pageOptions: { languageCode: langCode, isKiosk, isCustomDomain },
  } = useContext(StaticContext);
  const collectorId = sm?.smParams?.cid;
  const langId = sm?.smParams?.langId;
  const qrUrl = sm?.qaDoneUrl || '/';
  const smSlug = sm?.smStr || '';

  const {
    loading: loadingCollector,
    error: errorCollector,
    data: collectorData,
  } = useQuery(getSurveyForCollector, {
    context: {
      targetPath: '/lo-graphql/collector',
      headers: {
        'X-Collector-Id': collectorId,
        'X-Language-Id': langId,
      },
    },
    variables: {
      collectorId,
    },
  });

  const qzResultsHeaders = {
    'X-SM-PARAM': smSlug,
  };

  const {
    loading: loadingRespondent,
    error: errorRespondent,
    data: respondentData,
  } = useQuery(getQuizSurveyWithResponsesQM, {
    skip: !collectorData || loadingCollector,
    variables: {
      surveyId: collectorData?.collector?.surveyId,
      smParams: smSlug,
    },
    context: {
      targetPath: '/lo-graphql/quizResults',
      headers: qzResultsHeaders,
    },
  });

  if (
    errorCollector ||
    errorRespondent ||
    (!loadingRespondent && respondentData && respondentData.respondent === null)
  ) {
    return <FourOhFourErrorPage />;
  }

  const surveyData = respondentData?.survey;
  const quizScore = respondentData?.respondentObfuscated?.quizSummary;

  if (surveyData) {
    const surveyId = collectorData?.collector?.surveyId;
    const surveyLogo = surveyData?.design?.logo;
    const numberingEnabled = surveyData?.design?.questionNumbering?.enabled || false;
    const showQuizAnswers = surveyData?.quizOptions?.showAnswers || false;
    const scoreBase = surveyData?.quizOptions?.rangeType;
    const titleEnabled = surveyData?.design?.surveyTitle?.enabled || false;
    const footerEnabled = (surveyData?.design?.footer?.enabled && !isCustomDomain) || false;
    const surveyTitle = surveyData?.title || '';
    const surveyPages = surveyData?.pages?.items;
    const isGrayScale = surveyData?.design?.footer?.grayscale || false;
    const isRTL = ['ar', 'he', 'fa'].indexOf(langCode) > -1;
    const surveyResults = respondentData?.respondentObfuscated?.resultsQM;
    const surveyWithResults = formatSurvey(surveyResults, numberingEnabled, showQuizAnswers);
    const surveyTitleHTML = surveyData?.titleHTML || '';

    return (
      <BasePage
        actionFlow="QuizResults"
        pageId="QuizResults"
        legacyWeb="respweb"
        includeHeader={false}
        includeFooter={false}
      >
        <Helmet htmlAttributes={{ lang: langCode }}>
          <title>{surveyTitle} Survey</title>
          <meta name="description" content={t(COPY.META_DESCRIPTION)} />
          <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=yes" />
          <meta httpEquiv="content-language" content={langCode} />
        </Helmet>
        <SurveyTheme surveyId={surveyId} targetEndPoint="quizResults" headers={qzResultsHeaders}>
          <Layout isRTL={isRTL}>
            <Header logo={surveyLogo} />
            <QuizScoreSummary score={quizScore} surveyId={surveyId} base={scoreBase} />
            <If condition={titleEnabled}>
              <SurveyTitle title={surveyTitleHTML} />
            </If>
            <SurveyPages pages={surveyPages} survey={surveyWithResults} />
            <ActionGroup qrDoneUrl={qrUrl} />
            <If condition={footerEnabled}>
              <Footer isKiosk={isKiosk} isGrayScale={isGrayScale} />
            </If>
          </Layout>
        </SurveyTheme>
      </BasePage>
    );
  }
  return null;
};

export default QuizResult;
